import React, { useMemo } from 'react';
import Image from 'next/image';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import useModal from '@/hooks/useModal';
import LoginOrRegist from '@/containers/modal/login/LoginOrRegist';
import { SelectedIns, themeColorState } from '@/recoil/plan';
import { FontStyle } from '@/utils/StyleUtil';
import SvgSelector from '@/components/Svg/SvgSelector';
import Seperator from '@/components/Spacing/Seperator';
import { TEasyLogin } from '@/models';

interface IProps {
    removeCnt?: number;
    type: TEasyLogin;
}

function EasyLogin({ removeCnt = 0, type = 'consult' }: IProps) {
    const { addModal, removeCurrentModal } = useModal();
    const selIns = useRecoilValue(SelectedIns);
    const themeColor = useRecoilValue(themeColorState(selIns));

    const iconColor = useMemo(() => {
        switch (themeColor) {
            case '#4F88F6':
                return 'blue';
            case '#11BE80':
                return 'green';
            case '#CD0D5F':
                return 'wine';
            case '#0EC4CF':
            default:
                return 'mint';
        }
    }, [themeColor]);

    const renderContent = () => {
        if (type === 'direct') {
            return (
                <MatchingContentWrapper>
                    <ImageWrapper>
                        <Image
                            src={`/img/common/easy_login_${iconColor}@3x.png`}
                            alt="easy_login"
                            width="52px"
                            height="56px"
                        />
                    </ImageWrapper>
                    <div>
                        {'픽앤플랜의 전문가들은\n각종 보장에 대한\n'}
                        <strong>{'가장 좋은 상품을 제안드리며,\n'}</strong>
                        {'이미 가입한 보험도 컨설팅 받을 수 있어요'}
                    </div>
                    <Seperator height={12} />
                    <div>
                        <strong>{'간편 로그인'}</strong>
                        {'으로\n픽앤플랜의 서비스를 누려보세요!'}
                    </div>
                </MatchingContentWrapper>
            );
        } else if (type === 'request') {
            return (
                <MatchingContentWrapper>
                    <ImageWrapper>
                        <Image
                            src={`/img/common/easy_login_${iconColor}@3x.png`}
                            alt="easy_login"
                            width="52px"
                            height="56px"
                        />
                    </ImageWrapper>
                    <div className="title">선택 상품을 분석하여,</div>
                    <Seperator height={12} />
                    <div>
                        {'픽앤플랜의 전문가들은\n'}
                        <strong>{'더 좋은 상품을 제안 드립니다.\n'}</strong>
                        {'이미 가입한 보험도 컨설팅 받을 수 있어요'}
                    </div>
                    <Seperator height={12} />
                    <div>
                        <strong>{'간편 로그인'}</strong>
                        {'으로\n픽앤플랜의 서비스를 누려보세요!'}
                    </div>
                </MatchingContentWrapper>
            );
        } else if (type === 'consult') {
            return (
                <ConsultContentWrapper>
                    <ImageWrapper onClick={removeCurrentModal}>
                        <SvgSelector svg="close" width={18} height={18} stroke="#222222" strokeWidth={1.4} />
                    </ImageWrapper>
                    <div style={{ height: '22px' }} />
                    <div className="title">상담을 진행하려면</div>
                    <div className="title">간편 로그인이 필요합니다</div>
                </ConsultContentWrapper>
            );
        } else if (type === 'promotion') {
            return (
                <MatchingContentWrapper>
                    <ImageWrapper>
                        <Image
                            src={`/img/common/easy_login_${iconColor}@3x.png`}
                            alt="easy_login"
                            width="52px"
                            height="56px"
                        />
                    </ImageWrapper>
                    <div>
                        {'픽앤플랜의 전문가들은\n각종 보장에 대한\n'}
                        <strong>{'가장 좋은 상품을 제안드리며,\n'}</strong>
                        {'이미 가입한 보험도 컨설팅 받을 수 있어요'}
                    </div>
                    <Seperator height={12} />
                    <div>
                        <strong>{'간편 로그인'}</strong>
                        {'으로\n픽앤플랜의 서비스를 누려보세요!'}
                    </div>
                </MatchingContentWrapper>
            );
        }
    };

    return (
        <ModalContainer>
            {renderContent()}
            <LoginButton
                backgroundColor={themeColor}
                onClick={() => {
                    removeCurrentModal();

                    for (let i = 0; i < removeCnt; i += 1) removeCurrentModal();

                    addModal({
                        key: 'fullScreen',
                        props: {
                            content: <LoginOrRegist />,
                            handleCloseModal: removeCurrentModal,
                        },
                    });
                }}
            >
                간편 로그인
            </LoginButton>
        </ModalContainer>
    );
}

const ModalContainer = styled.div`
    padding: 20px 20px 40px 20px;
`;

const MatchingContentWrapper = styled.div`
    ${({ theme }) => theme.flex.flexColumn}
    padding: 30px 10px;
    ${FontStyle(16, 24, '400', '#686868', -1)};

    .title {
        font-weight: 700;
        color: #222222;
    }
    strong {
        color: #333;
        font-weight: 500;
    }
`;

const ConsultContentWrapper = styled.div`
    padding: 10px 10px 22px 10px;

    .title {
        ${FontStyle(22, 30, '700', '#111111', -1)};
    }
    .desc {
        margin-top: 8px;
        ${FontStyle(14, 22, '400', '#686868', -1)};
    }
`;

const ImageWrapper = styled.div`
    position: absolute;
    right: 30px;
    top: 30px;
`;

const LoginButton = styled.button<{ backgroundColor: string }>`
    width: 100%;
    height: 46px;
    background-color: ${({ backgroundColor }) => backgroundColor};
    border-radius: 8px;
    ${FontStyle(14, 18, '500', '#ffffff', -0.5)}
`;

export default EasyLogin;
